import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { hold, release } from "@/lib/idleSession";
import {
  type VideoInviteChannel,
  type VideoInviteOptions,
  type VideoInviteResponse,
  type VideoInviteResults,
} from "../lib/videoInvite";
import type {
  Room,
  LocalTrack,
  LocalVideoTrack,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
} from "twilio-video";

export type VideoCallStatus = "idle" | "connecting" | "connected" | "reconnecting" | "error";

export type VideoCallMode = "minimized" | "expanded";

export interface VideoCallContextValue {
  status: VideoCallStatus;
  errorMessage: string | null;
  roomName: string | null;
  joinUrl: string | null;
  /** Per-channel outcome of the invites actually attempted, once each replies. */
  inviteResults: VideoInviteResults | null;
  /** Which channels can reach this patient, resolved before the call starts. */
  inviteOptions: VideoInviteOptions | null;
  inviteOptionsLoading: boolean;
  /** Channels with a send in flight, so a button can show it is working. */
  invitePending: VideoInviteChannel[];
  isMuted: boolean;
  isCameraOff: boolean;
  isScreenSharing: boolean;
  // RefObject<T>, not RefObject<T | null>: the interface already declares
  // `readonly current: T | null`, so the union added nothing, and RefObject is
  // covariant in T — which made these unassignable to the `ref` prop of the
  // divs FloatingVideoCall and VideoCallPanel attach them to. Same shape the
  // useInViewport hook had.
  localVideoRef: React.RefObject<HTMLDivElement>;
  remoteVideoRef: React.RefObject<HTMLDivElement>;
  remoteParticipantCount: number;
  callDuration: number;
  mode: VideoCallMode;
  activePatientId: string | null;
  activePatientName: string | null;
  pendingConfirm: { patientId: string; patientName: string | null } | null;
  requestVideoCall: (patientId: string, patientName?: string) => void;
  confirmVideoCall: (channels: VideoInviteChannel[]) => void;
  cancelVideoCall: () => void;
  joinRoom: (patientId: string, patientName?: string, channels?: VideoInviteChannel[]) => Promise<void>;
  /** Send the invite again mid-call, on the channels given. */
  sendInvite: (channels: VideoInviteChannel[]) => Promise<void>;
  leaveRoom: () => void;
  dismissError: () => void;
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => Promise<void>;
  minimize: () => void;
  expand: () => void;
}

interface VideoTokenResponse {
  token: string;
  identity: string;
  roomName: string;
  roomSid: string;
  joinToken: string;
}

const VideoCallContext = createContext<VideoCallContextValue | null>(null);

export function useVideoCallContext(): VideoCallContextValue {
  const ctx = useContext(VideoCallContext);
  if (!ctx) {
    throw new Error("useVideoCallContext must be used within a VideoCallProvider");
  }
  return ctx;
}

export function VideoCallProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<VideoCallStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [inviteResults, setInviteResults] = useState<VideoInviteResults | null>(null);
  const [inviteOptions, setInviteOptions] = useState<VideoInviteOptions | null>(null);
  const [inviteOptionsLoading, setInviteOptionsLoading] = useState(false);
  const [invitePending, setInvitePending] = useState<VideoInviteChannel[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [remoteParticipantCount, setRemoteParticipantCount] = useState(0);
  const [callDuration, setCallDuration] = useState(0);
  const [mode, setMode] = useState<VideoCallMode>("expanded");
  const [activePatientId, setActivePatientId] = useState<string | null>(null);
  const [activePatientName, setActivePatientName] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{ patientId: string; patientName: string | null } | null>(null);

  // Hold the idle-logout timer open for the duration of a call. A clinician on a
  // 15-minute video visit generates no pointer or key events, so without this
  // they'd be warned and signed out mid-consultation. Memory-only and capped —
  // see lib/idleSession.ts.
  useEffect(() => {
    if (status === "idle") return;
    hold("video-call");
    return () => release("video-call");
  }, [status]);

  const roomRef = useRef<Room | null>(null);
  const localTracksRef = useRef<LocalTrack[]>([]);
  const screenTrackRef = useRef<LocalVideoTrack | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callStartRef = useRef<number>(0);
  const patientIdRef = useRef<string | null>(null);
  const roomSidRef = useRef<string | null>(null);
  const roomNameRef = useRef<string | null>(null);
  const callLoggedRef = useRef(false);
  /** Channels staff picked at the confirmation, so a retry repeats that choice. */
  const inviteChannelsRef = useRef<VideoInviteChannel[]>([]);

  const localVideoRef = useRef<HTMLDivElement>(null);
  const remoteVideoRef = useRef<HTMLDivElement>(null);

  const startTimer = useCallback(() => {
    callStartRef.current = Date.now();
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    callStartRef.current = 0;
  }, []);

  const attachLocalTracks = useCallback((tracks: LocalTrack[]) => {
    const container = localVideoRef.current;
    if (!container) return;

    tracks.forEach((track) => {
      if (track.kind === "video") {
        const el = (track as LocalVideoTrack).attach();
        el.style.width = "100%";
        el.style.height = "100%";
        el.style.objectFit = "cover";
        el.style.transform = "scaleX(-1)";
        container.appendChild(el);
      }
    });
  }, []);

  const attachRemoteTrack = useCallback((track: RemoteTrack) => {
    const container = remoteVideoRef.current;
    if (!container) return;
    if (track.kind === "video" || track.kind === "audio") {
      const el = (track as { attach: () => HTMLMediaElement }).attach();
      if (track.kind === "video") {
        el.style.width = "100%";
        el.style.height = "100%";
        el.style.objectFit = "cover";
      }
      el.dataset.trackSid = track.sid;
      container.appendChild(el);
    }
  }, []);

  const detachRemoteTrack = useCallback((track: RemoteTrack) => {
    const container = remoteVideoRef.current;
    if (!container) return;
    const elements = container.querySelectorAll(`[data-track-sid="${track.sid}"]`);
    elements.forEach((el) => el.remove());
  }, []);

  const handleParticipantConnected = useCallback(
    (participant: RemoteParticipant) => {
      participant.tracks.forEach((publication: RemoteTrackPublication) => {
        if (publication.isSubscribed && publication.track) {
          attachRemoteTrack(publication.track);
        }
      });

      participant.on("trackSubscribed", (track: RemoteTrack) => {
        attachRemoteTrack(track);
      });

      participant.on("trackUnsubscribed", (track: RemoteTrack) => {
        detachRemoteTrack(track);
      });

      setRemoteParticipantCount((c) => c + 1);
    },
    [attachRemoteTrack, detachRemoteTrack],
  );

  const handleParticipantDisconnected = useCallback(
    (participant: RemoteParticipant) => {
      participant.tracks.forEach((publication: RemoteTrackPublication) => {
        if (publication.track) {
          detachRemoteTrack(publication.track);
        }
      });
      setRemoteParticipantCount((c) => Math.max(0, c - 1));
    },
    [detachRemoteTrack],
  );

  const logVideoCall = useCallback(async () => {
    if (callLoggedRef.current) return;
    const pid = patientIdRef.current;
    const rSid = roomSidRef.current;
    const rName = roomNameRef.current;
    if (!pid || !rSid) return;

    callLoggedRef.current = true;
    const duration = callStartRef.current
      ? Math.floor((Date.now() - callStartRef.current) / 1000)
      : 0;

    try {
      await api("/commlog/videocalls", {
        method: "POST",
        body: { patientID: pid, roomSid: rSid, roomName: rName || "", duration },
        noRedirectOn401: true,
      });
    } catch (err) {
      console.error("VideoCallContext: Failed to log video call", err);
    }
  }, []);

  const cleanupRoom = useCallback(() => {
    const room = roomRef.current;
    if (room) {
      room.disconnect();
      roomRef.current = null;
    }

    localTracksRef.current.forEach((track) => {
      if ("stop" in track && typeof track.stop === "function") {
        track.stop();
      }
    });
    localTracksRef.current = [];

    if (screenTrackRef.current) {
      screenTrackRef.current.stop();
      screenTrackRef.current = null;
    }

    if (localVideoRef.current) localVideoRef.current.innerHTML = "";
    if (remoteVideoRef.current) remoteVideoRef.current.innerHTML = "";

    stopTimer();
    setStatus("idle");
    setErrorMessage(null);
    setRoomName(null);
    setJoinUrl(null);
    setInviteResults(null);
    setInviteOptions(null);
    setInviteOptionsLoading(false);
    setInvitePending([]);
    setIsMuted(false);
    setIsCameraOff(false);
    setIsScreenSharing(false);
    setRemoteParticipantCount(0);
    setCallDuration(0);
    setActivePatientId(null);
    setActivePatientName(null);
  }, [stopTimer]);

  /**
   * Ask the API to send the invite and record what came back for each channel.
   *
   * Never throws: the call is already connecting and must not be interrupted
   * because a text bounced. It does record the failure though — this used to
   * drop into console.warn, so a patient with no mobile number on file produced
   * a call screen that said nothing at all.
   */
  const postInvite = useCallback(async (patientId: string, channels: VideoInviteChannel[]) => {
    if (channels.length === 0) return;

    setInvitePending((pending) => [...pending, ...channels.filter((c) => !pending.includes(c))]);

    try {
      const inviteData = await api<VideoInviteResponse>("/video-invite", {
        method: "POST",
        body: { patientID: patientId, channels },
        noRedirectOn401: true,
      });

      if (inviteData.joinUrl) setJoinUrl(inviteData.joinUrl);
      setInviteResults((previous) => ({ ...(previous ?? {}), ...(inviteData.results ?? {}) }));
    } catch (inviteErr) {
      console.warn("VideoCallContext: video invite failed", inviteErr);
      setInviteResults((previous) => ({
        ...(previous ?? {}),
        ...Object.fromEntries(channels.map((c) => [c, { sent: false, reason: "send_failed" }])),
      }));
    } finally {
      setInvitePending((pending) => pending.filter((c) => !channels.includes(c)));
    }
  }, []);

  const joinRoom = useCallback(
    async (patientId: string, patientName?: string, channels?: VideoInviteChannel[]) => {
      if (status !== "idle") return;
      // Retrying after an error re-sends on whatever staff originally chose:
      // FloatingVideoCall's "Try Again" calls this with no channels, and
      // cleanupRoom deliberately leaves this ref alone so the choice survives.
      const inviteChannels = channels ?? inviteChannelsRef.current;
      inviteChannelsRef.current = inviteChannels;
      setStatus("connecting");
      setErrorMessage(null);
      setActivePatientId(patientId);
      setActivePatientName(patientName ?? null);
      setMode("expanded");

      let tokenData: VideoTokenResponse;
      try {
        tokenData = await api<VideoTokenResponse>("/video-token", {
          params: { patientID: patientId },
          noRedirectOn401: true,
        });
      } catch (err) {
        console.error("VideoCallContext: Failed to get token", err);
        setStatus("error");
        setErrorMessage("Failed to initialize video call. Please try again.");
        return;
      }

      patientIdRef.current = patientId;
      roomSidRef.current = tokenData.roomSid || null;
      roomNameRef.current = tokenData.roomName;
      callLoggedRef.current = false;

      setRoomName(tokenData.roomName);
      const computedJoinUrl = `${window.location.origin}/video-join?t=${encodeURIComponent(tokenData.joinToken)}`;
      setJoinUrl(computedJoinUrl);

      void postInvite(patientId, inviteChannels);

      const Video = await import("twilio-video");
      let localTracks: LocalTrack[] = [];
      try {
        localTracks = await Video.createLocalTracks({
          audio: true,
          video: { width: 640, height: 480, frameRate: 24 },
        });
      } catch (videoErr) {
        console.warn("VideoCallContext: Camera unavailable, trying audio-only", videoErr);
        try {
          localTracks = await Video.createLocalTracks({ audio: true, video: false });
          setIsCameraOff(true);
        } catch (audioErr) {
          console.warn("VideoCallContext: Mic also unavailable, connecting without local media", audioErr);
        }
      }

      localTracksRef.current = localTracks;

      try {
        const room = await Video.connect(tokenData.token, {
          name: tokenData.roomName,
          tracks: localTracks,
          dominantSpeaker: true,
        });

        roomRef.current = room;
        setStatus("connected");
        startTimer();

        attachLocalTracks(localTracks);

        room.participants.forEach(handleParticipantConnected);
        room.on("participantConnected", handleParticipantConnected);
        room.on("participantDisconnected", handleParticipantDisconnected);

        room.on("reconnecting", () => setStatus("reconnecting"));
        room.on("reconnected", () => setStatus("connected"));

        room.on("disconnected", () => {
          logVideoCall();
          cleanupRoom();
        });
      } catch (err) {
        console.error("VideoCallContext: Failed to connect to room", err);
        localTracks.forEach((t) => { if ("stop" in t && typeof t.stop === "function") t.stop(); });
        localTracksRef.current = [];
        setStatus("error");
        // Do not claim the link reached the patient: staff may have started the
        // call with no invite ticked, and the panel below reports each channel.
        setErrorMessage("Could not connect to the video call. Copy the join link below and send it to the patient.");
      }
    },
    [status, startTimer, attachLocalTracks, handleParticipantConnected, handleParticipantDisconnected, logVideoCall, cleanupRoom, postInvite],
  );

  const requestVideoCall = useCallback((patientId: string, patientName?: string) => {
    if (status !== "idle") {
      setMode("expanded");
      return;
    }
    setPendingConfirm({ patientId, patientName: patientName ?? null });

    // Resolve what can actually reach this patient while the dialog is open, so
    // staff are offered the channels that work and told about the ones that do
    // not — rather than picking one that was never going to arrive.
    setInviteOptions(null);
    setInviteOptionsLoading(true);
    api<VideoInviteOptions>("/video-invite/options", {
      params: { patientID: patientId },
      noRedirectOn401: true,
    })
      .then((options) => setInviteOptions(options))
      .catch((err) => {
        // Leaving this null makes the dialog offer both channels unchecked
        // against; the send itself still reports the truth.
        console.warn("VideoCallContext: could not resolve invite options", err);
        setInviteOptions(null);
      })
      .finally(() => setInviteOptionsLoading(false));
  }, [status]);

  const confirmVideoCall = useCallback((channels: VideoInviteChannel[]) => {
    const pending = pendingConfirm;
    setPendingConfirm(null);
    if (pending) {
      void joinRoom(pending.patientId, pending.patientName ?? undefined, channels);
    }
  }, [pendingConfirm, joinRoom]);

  const sendInvite = useCallback(async (channels: VideoInviteChannel[]) => {
    const patientId = patientIdRef.current;
    if (!patientId) return;
    await postInvite(patientId, channels);
  }, [postInvite]);

  const cancelVideoCall = useCallback(() => {
    setPendingConfirm(null);
  }, []);

  const leaveRoom = useCallback(() => {
    logVideoCall();
    cleanupRoom();
  }, [logVideoCall, cleanupRoom]);

  const dismissError = useCallback(() => {
    cleanupRoom();
  }, [cleanupRoom]);

  const toggleMute = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;

    room.localParticipant.audioTracks.forEach((pub) => {
      if (isMuted) {
        pub.track.enable();
      } else {
        pub.track.disable();
      }
    });
    setIsMuted((m) => !m);
  }, [isMuted]);

  const toggleCamera = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;

    room.localParticipant.videoTracks.forEach((pub) => {
      if (isCameraOff) {
        pub.track.enable();
      } else {
        pub.track.disable();
      }
    });
    setIsCameraOff((c) => !c);
  }, [isCameraOff]);

  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;

    if (isScreenSharing && screenTrackRef.current) {
      room.localParticipant.unpublishTrack(screenTrackRef.current);
      screenTrackRef.current.stop();
      screenTrackRef.current = null;
      setIsScreenSharing(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const Video = await import("twilio-video");
      const screenTrack = new Video.LocalVideoTrack(stream.getTracks()[0], {
        name: "screen-share",
      });

      await room.localParticipant.publishTrack(screenTrack);
      screenTrackRef.current = screenTrack;
      setIsScreenSharing(true);

      screenTrack.mediaStreamTrack.addEventListener("ended", () => {
        if (roomRef.current) {
          roomRef.current.localParticipant.unpublishTrack(screenTrack);
        }
        screenTrack.stop();
        screenTrackRef.current = null;
        setIsScreenSharing(false);
      });
    } catch (err) {
      console.error("Screen share failed:", err);
    }
  }, [isScreenSharing]);

  const minimize = useCallback(() => setMode("minimized"), []);
  const expand = useCallback(() => setMode("expanded"), []);

  useEffect(() => {
    return () => {
      const room = roomRef.current;
      if (room) {
        room.disconnect();
      }
      localTracksRef.current.forEach((track) => {
        if ("stop" in track && typeof track.stop === "function") {
          track.stop();
        }
      });
      if (screenTrackRef.current) {
        screenTrackRef.current.stop();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const value: VideoCallContextValue = {
    status,
    errorMessage,
    roomName,
    joinUrl,
    inviteResults,
    inviteOptions,
    inviteOptionsLoading,
    invitePending,
    isMuted,
    isCameraOff,
    isScreenSharing,
    localVideoRef,
    remoteVideoRef,
    remoteParticipantCount,
    callDuration,
    mode,
    activePatientId,
    activePatientName,
    pendingConfirm,
    requestVideoCall,
    confirmVideoCall,
    cancelVideoCall,
    joinRoom,
    sendInvite,
    leaveRoom,
    dismissError,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    minimize,
    expand,
  };

  return (
    <VideoCallContext.Provider value={value}>
      {children}
    </VideoCallContext.Provider>
  );
}
